/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOpenDrawer';

const ActionOpenDrawerManager: ActionManagerFn<'ActionOpenDrawer'> = (
  platform: Platform,
): ActionManager<'ActionOpenDrawer'> => {
  return {
    ...baseActionManager(platform, 'ActionOpenDrawer'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Drawer Item ID',
        type: 'ItemID',
        initialFilter: 'Drawer',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Drawer') {
            return `Item id '${value}' doesn't belong to a Drawer`;
          }
          return '';
        },
      },
    },
    name: 'Open Drawer',
    isItemIdUsed: (item, itemId) => item.props.targetItemId === itemId,
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'window-restore' },
    description: `**Overview**<br/>
The Open Drawer action is a UI interaction component designed to programmatically activate a specific "Drawer" component on the page. A Drawer is typically a slide-out panel (appearing from the side or bottom) used to present supplementary content, forms, or details without navigating away from the main view. This action functions as the trigger mechanism to reveal these initially hidden panels.<br/><br/>
This component is tightly coupled with the Drawer UI component type. It ensures that the target exists and is strictly of the correct type, preventing configuration errors where a developer might accidentally try to "Open Drawer" on a button or a text field.

**Key Features**<br/>
* **Targeted Activation:** Specifically designed to trigger the animation and display logic of a Drawer component.
* **Strict Type Validation:** Implements validateItemId to ensure the selected targetItemId is explicitly a Drawer. If a different component type is selected, the builder returns an error.
* **Registry Filtering:** The property configuration uses initialFilter: 'Drawer', making it easier to find the correct component by filtering the dropdown list to show only valid Drawers.
* **Dependency Tracking:** Automatically registers a dependency between the action and the target Drawer, ensuring the platform understands the relationship for validation and refactoring.

**Properties**<br/>
* **Drawer Item ID (targetItemId):** A property of type ItemID.
  * *Function:* Identifies the unique ID of the Drawer component to be opened.
  * *Validation:* Must reference a valid component of type 'Drawer'.

**Use Cases**<br/>
* **Master-Detail Views:** Clicking a row in a data grid to slide out a Drawer containing the full details and edit form for that specific record.
* **Advanced Filtering:** A "Filters" button on the main toolbar that opens a side drawer containing complex search criteria and inputs.
* **Contextual Help:** A help icon that opens a Drawer containing instructions or documentation relevant to the current screen.`,
  };
};

export default ActionOpenDrawerManager;
